import { z } from 'zod';

export const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/)
  .describe('Six-digit HEX color.');
export const paletteSchema = z.strictObject({
  base: hexColorSchema,
  steps: z.array(hexColorSchema).length(12),
  contrast: hexColorSchema,
});
export type Palette = z.infer<typeof paletteSchema>;
export const neutralColorPresetSchema = z.enum(['Gray', 'GreenGray', 'BlueGray', 'GoldGray']);
export const defaultPalettes: Record<'primary' | 'secondary', Palette> = {
  primary: {
    base: '#3e63dd',
    steps: [
      '#fdfdfe',
      '#f7f9ff',
      '#edf2fe',
      '#e1e9ff',
      '#d2deff',
      '#c1d0ff',
      '#abbdf9',
      '#8da4ef',
      '#3e63dd',
      '#3358d4',
      '#3a5bc7',
      '#1f2d5c',
    ],
    contrast: '#ffffff',
  },
  secondary: {
    base: '#e5484d',
    steps: [
      '#fffcfc',
      '#fff7f7',
      '#feebec',
      '#ffdbdc',
      '#ffcdce',
      '#fdbdbe',
      '#f4a9aa',
      '#eb8e90',
      '#e5484d',
      '#dc3e42',
      '#ce2c31',
      '#641723',
    ],
    contrast: '#000000',
  },
};
export const neutralScales = {
  Gray: [
    '#fcfcfc',
    '#f9f9f9',
    '#f0f0f0',
    '#e8e8e8',
    '#e0e0e0',
    '#d9d9d9',
    '#cecece',
    '#bbbbbb',
    '#8d8d8d',
    '#838383',
    '#646464',
    '#202020',
  ],
  GreenGray: [
    '#fbfdfc',
    '#f7f9f8',
    '#eef1f0',
    '#e6e9e8',
    '#dfe2e0',
    '#d7dad9',
    '#cbcfcd',
    '#b8bcba',
    '#868e8b',
    '#7c8481',
    '#5f6563',
    '#1a211e',
  ],
  BlueGray: [
    '#fcfcfd',
    '#f9f9fb',
    '#f0f0f3',
    '#e8e8ec',
    '#e0e1e6',
    '#d9d9e0',
    '#cdced6',
    '#b9bbc6',
    '#8b8d98',
    '#80838d',
    '#60646c',
    '#1c2024',
  ],
  GoldGray: [
    '#fdfdfc',
    '#f9f9f8',
    '#f1f0ef',
    '#e9e8e6',
    '#e2e1de',
    '#dad9d6',
    '#cfceca',
    '#bcbbb5',
    '#8d8d86',
    '#82827c',
    '#63635e',
    '#21201c',
  ],
};
export function contrastRatio(first: string, second: string): number {
  const luminance = (hex: string) => {
    const channels = [1, 3, 5]
      .map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
      .map((c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
    return channels[0]! * 0.2126 + channels[1]! * 0.7152 + channels[2]! * 0.0722;
  };
  const a = luminance(first),
    b = luminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}
